/**
 * Utilidades para API de Roadmaps
 */

// Configuração da API (igual ao useAuth.js)
const API_URL = process.env.NODE_ENV === 'production' 
  ? 'https://dashsimples.vercel.app/api' // Sempre usar Vercel para APIs
  : 'http://localhost:3001/api';

class RoadmapAPIError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'RoadmapAPIError';
    this.status = status;
  }
}

/**
 * Função base para fazer requisições à API
 */
async function apiRequest(endpoint, options = {}) {
  const url = `${API_URL}/${endpoint}`;
  
  // Obter token do localStorage
  const token = localStorage.getItem('auth_token');
  
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include'
  };

  // Incluir token se disponível
  if (token) {
    defaultOptions.headers['Authorization'] = `Bearer ${token}`;
  }

  const config = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers
    }
  };

  try {
    const response = await fetch(url, config);
    const data = await response.json();

    if (!response.ok) {
      throw new RoadmapAPIError(
        data.error || `Erro ${response.status}`,
        response.status
      );
    }

    if (!data.success) {
      throw new RoadmapAPIError(data.error || 'Erro desconhecido');
    }

    return data;
  } catch (error) {
    if (error instanceof RoadmapAPIError) {
      throw error;
    }
    
    // Erro de rede ou outro erro
    console.error('Erro na requisição:', error);
    throw new RoadmapAPIError('Erro de conexão com o servidor');
  }
}

/**
 * API de Roadmaps
 */
export const roadmapAPI = {
  /**
   * Salvar ou atualizar roadmap
   */
  async save(roadmapData) {
    // 🐛 DEBUG: Log dos dados recebidos
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 [DEBUG API] Dados recebidos para salvamento:', {
        name: roadmapData.name,
        totalItems: roadmapData.items?.length || 0,
        mainItems: roadmapData.items?.filter(item => (item.level ?? 0) === 0).length || 0,
        subItems: roadmapData.items?.filter(item => (item.level ?? 0) === 1).length || 0,
        roadmapId: roadmapData.roadmapId,
        items: roadmapData.items
      });

      // 🔍 DEBUG: Verificar subitens antes do envio
      const subitens = roadmapData.items?.filter(item => (item.level ?? 0) === 1) || [];
      if (subitens.length > 0) {
        console.log('🌳 [DEBUG API] Subitens sendo enviados:');
        subitens.forEach(subitem => {
          console.log(`  - "${subitem.Nome}" (level: ${subitem.level}, parentId: ${subitem.parentId})`);
        });
      }
    }

    return apiRequest('roadmap-save-simple', {
      method: 'POST',
      body: JSON.stringify(roadmapData)
    });
  },

  /**
   * Listar roadmaps do usuário
   */
  async list(params = {}) {
    const searchParams = new URLSearchParams();
    
    if (params.page) searchParams.append('page', params.page);
    if (params.limit) searchParams.append('limit', params.limit);
    if (params.search) searchParams.append('search', params.search);
    
    const query = searchParams.toString();
    const endpoint = query ? `roadmap-list?${query}` : 'roadmap-list';
    
    return apiRequest(endpoint, {
      method: 'GET'
    });
  },

  /**
   * Carregar roadmap específico
   */
  async load(id) {
    return apiRequest(`roadmap-load?id=${id}`, {
      method: 'GET'
    });
  },

  /**
   * Deletar roadmap
   */
  async delete(id) {
    return apiRequest(`roadmap-delete?id=${id}`, {
      method: 'DELETE'
    });
  }
};

export { RoadmapAPIError };
